import { FeatureCollection, Point } from 'geojson'
import { useMemo } from 'react'
import { Layer, Source } from 'react-map-gl'
import { useSelector } from 'react-redux'
import { selectHighlightedFeatureIdInList } from 'store/selectors/mission'
import { DEFAULT_VIZUALIZATION_SETTINGS } from 'utils/constants'
import { ILaunchPoint, IRoute } from 'utils/interfaces'

// Finish point (Bullseye pattern: outer circle -> white ring -> inner dot)
const cleaningRouteFinishPointStyle1: any = {
  id: 'gl-draw-cleaning-route-finish-point1',
  type: 'circle',
  paint: {
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      14,
      [
        'case',
        ['boolean', ['get', 'has_selection'], false],
        ['case', ['boolean', ['get', 'is_selected'], false], 2.5, 1.5],
        2.5,
      ],
      20,
      [
        'case',
        ['boolean', ['get', 'has_selection'], false],
        ['case', ['boolean', ['get', 'is_selected'], false], 7.5, 4.5],
        7.5,
      ],
    ],
    'circle-color': ['coalesce', ['get', 'color'], DEFAULT_VIZUALIZATION_SETTINGS.cleaning_route_color],
    'circle-opacity': [
      'case',
      ['boolean', ['get', 'is_selected'], false],
      1.0,
      ['boolean', ['get', 'has_selection'], false],
      0.25,
      0.85,
    ],
  },
}

const cleaningRouteFinishPointStyle2: any = {
  id: 'gl-draw-cleaning-route-finish-point2',
  type: 'circle',
  paint: {
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      14,
      [
        'case',
        ['boolean', ['get', 'has_selection'], false],
        ['case', ['boolean', ['get', 'is_selected'], false], 1.6, 0.9],
        1.6,
      ],
      20,
      [
        'case',
        ['boolean', ['get', 'has_selection'], false],
        ['case', ['boolean', ['get', 'is_selected'], false], 5.0, 3.0],
        5.0,
      ],
    ],
    'circle-color': '#ffffff',
    'circle-opacity': [
      'case',
      ['boolean', ['get', 'is_selected'], false],
      1.0,
      ['boolean', ['get', 'has_selection'], false],
      0.25,
      0.85,
    ],
  },
}

const cleaningRouteFinishPointStyle3: any = {
  id: 'gl-draw-cleaning-route-finish-point3',
  type: 'circle',
  paint: {
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      14,
      [
        'case',
        ['boolean', ['get', 'has_selection'], false],
        ['case', ['boolean', ['get', 'is_selected'], false], 0.8, 0.5],
        0.8,
      ],
      20,
      [
        'case',
        ['boolean', ['get', 'has_selection'], false],
        ['case', ['boolean', ['get', 'is_selected'], false], 2.8, 1.6],
        2.8,
      ],
    ],
    'circle-color': ['coalesce', ['get', 'color'], DEFAULT_VIZUALIZATION_SETTINGS.cleaning_route_color],
    'circle-opacity': [
      'case',
      ['boolean', ['get', 'is_selected'], false],
      1.0,
      ['boolean', ['get', 'has_selection'], false],
      0.25,
      0.85,
    ],
  },
}

// Launch / Start point (Dot in circle pattern: outer circle -> white center)
const cleaningRouteLaunchPointStyle1: any = {
  id: 'gl-draw-cleaning-route-launch-point',
  type: 'circle',
  paint: {
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      14,
      [
        'case',
        ['boolean', ['get', 'has_selection'], false],
        ['case', ['boolean', ['get', 'is_selected'], false], 3.2, 2.0],
        3.2,
      ],
      20,
      [
        'case',
        ['boolean', ['get', 'has_selection'], false],
        ['case', ['boolean', ['get', 'is_selected'], false], 9.5, 5.5],
        9.5,
      ],
    ],
    'circle-color': ['coalesce', ['get', 'color'], DEFAULT_VIZUALIZATION_SETTINGS.cleaning_route_color],
    'circle-opacity': [
      'case',
      ['boolean', ['get', 'is_selected'], false],
      1.0,
      ['boolean', ['get', 'has_selection'], false],
      0.25,
      0.85,
    ],
  },
}

const cleaningRouteLaunchPointStyle2: any = {
  id: 'gl-draw-cleaning-route-launch-point-center',
  type: 'circle',
  paint: {
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      14,
      [
        'case',
        ['boolean', ['get', 'has_selection'], false],
        ['case', ['boolean', ['get', 'is_selected'], false], 1.6, 1.0],
        1.6,
      ],
      20,
      [
        'case',
        ['boolean', ['get', 'has_selection'], false],
        ['case', ['boolean', ['get', 'is_selected'], false], 5.0, 3.0],
        5.0,
      ],
    ],
    'circle-color': '#ffffff',
    'circle-opacity': [
      'case',
      ['boolean', ['get', 'is_selected'], false],
      1.0,
      ['boolean', ['get', 'has_selection'], false],
      0.25,
      0.85,
    ],
  },
}

const CleaningPointsOnMap = ({
  cleaningRoutes,
  launchPoints,
  editingRouteId,
  highlightedName,
  beforeId,
}: {
  cleaningRoutes: IRoute[]
  launchPoints: ILaunchPoint[]
  editingRouteId?: number
  highlightedName?: string
  beforeId?: string
}) => {
  const highlightedFeatureIdInList = useSelector(selectHighlightedFeatureIdInList)

  const isRouteSelected = (r: IRoute): boolean => {
    if (editingRouteId && r.id === editingRouteId) return true
    if ((r.properties as any)?.highlighted) return true
    if (highlightedName && r.properties?.name === highlightedName) return true
    if (highlightedFeatureIdInList != null) {
      if (r.id === highlightedFeatureIdInList) return true
      if (r.properties?.zone_id === highlightedFeatureIdInList) return true
      if (r.properties?.id_on_map === highlightedFeatureIdInList) return true
      if (r.properties?.name === highlightedFeatureIdInList) return true
    }
    return false
  }

  const effectiveHasSelection = useMemo(() => {
    return (
      !!editingRouteId ||
      highlightedFeatureIdInList != null ||
      !!highlightedName ||
      cleaningRoutes.some(r => isRouteSelected(r))
    )
  }, [cleaningRoutes, editingRouteId, highlightedName, highlightedFeatureIdInList])

  const cleaningRoutesFinishPointsFeatureCollections: FeatureCollection<Point> = useMemo(() => {
    const rawFeatures = cleaningRoutes.reduce((acc, cleaningRoute) => {
      const geom = cleaningRoute.geometry as any
      if (!geom?.coordinates?.length) return acc
      let finishCoord: [number, number] | null = null
      if (geom.type === 'LineString') {
        finishCoord = geom.coordinates[geom.coordinates.length - 1]
      } else if (geom.type === 'MultiLineString') {
        const lastLine = geom.coordinates[geom.coordinates.length - 1]
        if (lastLine?.length) {
          finishCoord = lastLine[lastLine.length - 1]
        }
      }
      if (!finishCoord || !Array.isArray(finishCoord) || finishCoord.length < 2) return acc

      acc.push({
        id: cleaningRoute.id + '-finish-point',
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: finishCoord,
        },
        properties: {
          id_on_map: cleaningRoute.properties?.id_on_map,
          color: (cleaningRoute.properties as any)?.color,
          is_selected: isRouteSelected(cleaningRoute),
          has_selection: effectiveHasSelection,
        },
      })
      return acc
    }, [] as any[])

    rawFeatures.sort((a, b) =>
      a.properties.is_selected === b.properties.is_selected ? 0 : a.properties.is_selected ? 1 : -1
    )

    return {
      type: 'FeatureCollection',
      features: rawFeatures,
    }
  }, [cleaningRoutes, editingRouteId, highlightedName, highlightedFeatureIdInList, effectiveHasSelection])

  const cleaningRoutesLaunchPointsFeatureCollections: FeatureCollection<Point> = useMemo(() => {
    const rawFeatures: any[] = []

    launchPoints.forEach(launchPoint => {
      rawFeatures.push(launchPoint)
    })

    cleaningRoutes.forEach(cleaningRoute => {
      const launchPointExisting = launchPoints.find(
        launchPoint => launchPoint.properties?.zone_id === cleaningRoute.properties?.zone_id
      )
      if (!launchPointExisting) {
        const geom = cleaningRoute.geometry as any
        if (!geom?.coordinates?.length) return
        let startCoord: [number, number] | null = null
        if (geom.type === 'LineString') {
          startCoord = geom.coordinates[0]
        } else if (geom.type === 'MultiLineString') {
          startCoord = geom.coordinates[0]?.[0]
        }
        if (!startCoord || !Array.isArray(startCoord) || startCoord.length < 2) return

        rawFeatures.push({
          id: cleaningRoute.id + '-launch-point',
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: startCoord,
          },
          properties: {
            id_on_map: cleaningRoute.properties?.id_on_map,
            color: (cleaningRoute.properties as any)?.color,
            is_selected: isRouteSelected(cleaningRoute),
            has_selection: effectiveHasSelection,
          },
        })
      }
    })

    rawFeatures.sort((a, b) =>
      a.properties?.is_selected === b.properties?.is_selected ? 0 : a.properties?.is_selected ? 1 : -1
    )

    return {
      type: 'FeatureCollection',
      features: rawFeatures,
    }
  }, [cleaningRoutes, launchPoints, editingRouteId, highlightedName, highlightedFeatureIdInList, effectiveHasSelection])

  return (
    <>
      <Source id="cleaning-routes-finish-points" type="geojson" data={cleaningRoutesFinishPointsFeatureCollections}>
        <Layer {...cleaningRouteFinishPointStyle1} beforeId={beforeId} />
        <Layer {...cleaningRouteFinishPointStyle2} beforeId={beforeId} />
        <Layer {...cleaningRouteFinishPointStyle3} beforeId={beforeId} />
      </Source>
      <Source id="cleaning-routes-launch-points" type="geojson" data={cleaningRoutesLaunchPointsFeatureCollections}>
        <Layer {...cleaningRouteLaunchPointStyle1} beforeId={beforeId} />
        <Layer {...cleaningRouteLaunchPointStyle2} beforeId={beforeId} />
      </Source>
    </>
  )
}

export default CleaningPointsOnMap
